import { useCallback, useEffect, useMemo, useState } from 'react';

import { getSelectedChannelValues } from '../utils';

/**
 * Selection model for the Manage Booking Channels drawer.
 *
 * - `disabledValues` is what the API says to SKIP; the baseline is its inverse,
 *   i.e. the channels that should render as checked.
 * - `selected` is the user's working copy, reset to the baseline whenever the
 *   drawer opens or the saved value changes, so a discarded edit never lingers.
 * - `hasChanges` compares against the baseline rather than counting clicks, so
 *   unchecking a channel and re-checking it counts as unchanged.
 * - `selectAll` / `clearAll` take the currently visible values, which is what
 *   makes them respect an active search without knowing anything about it.
 */
export function useBookingChannelSelection({ channels, disabledValues, open }) {
    // Keyed on the baseline's *contents*, not the identity of `disabledValues`:
    // a caller handing back a fresh array each render must not wipe the user's
    // in-progress edits via the reset effect below.
    const baselineValues = useMemo(
        () => getSelectedChannelValues(channels, disabledValues),
        [channels, disabledValues]
    );
    const baselineKey = baselineValues.join('|');
    const baseline = useMemo(
        () => new Set(baselineKey ? baselineKey.split('|') : []),
        [baselineKey]
    );

    const [selected, setSelected] = useState(baseline);

    useEffect(() => {
        setSelected(new Set(baseline));
    }, [baseline, open]);

    const isSelected = useCallback((value) => selected.has(value), [selected]);

    const toggle = useCallback((value) => {
        setSelected((current) => {
            const next = new Set(current);
            if (next.has(value)) next.delete(value);
            else next.add(value);
            return next;
        });
    }, []);

    const selectAll = useCallback((values) => {
        setSelected((current) => new Set([...current, ...values]));
    }, []);

    const clearAll = useCallback((values) => {
        setSelected((current) => {
            const next = new Set(current);
            values.forEach((value) => next.delete(value));
            return next;
        });
    }, []);

    const reset = useCallback(() => {
        setSelected(new Set(baseline));
    }, [baseline]);

    const hasChanges = useMemo(() => {
        if (selected.size !== baseline.size) return true;
        for (const value of baseline) {
            if (!selected.has(value)) return true;
        }
        return false;
    }, [selected, baseline]);

    const selectedValues = useMemo(() => [...selected], [selected]);

    // Drive the disabled states of "Select all" / "Clear all" from the visible rows.
    const allSelected = useCallback(
        (values) => values.length > 0 && values.every((value) => selected.has(value)),
        [selected]
    );

    const noneSelected = useCallback(
        (values) => values.length > 0 && !values.some((value) => selected.has(value)),
        [selected]
    );

    return {
        selectedValues,
        selectedCount: selected.size,
        isSelected,
        toggle,
        selectAll,
        clearAll,
        reset,
        hasChanges,
        allSelected,
        noneSelected,
    };
}

export default useBookingChannelSelection;
